import { z } from 'zod';

/** Workspace member roles (§9). */
export const Role = z.enum(['owner', 'admin', 'performer', 'viewer']);
export type Role = z.infer<typeof Role>;

/** Fine-grained optional permissions layered on top of a role (§9.7). */
export const Permission = z.enum([
  'manage_repertoire',
  'publish_gigs',
  'configure_payments',
  'export_data',
]);
export type Permission = z.infer<typeof Permission>;

export const WorkspaceStatus = z.enum(['active', 'suspended', 'deleting', 'deleted']);
export type WorkspaceStatus = z.infer<typeof WorkspaceStatus>;

export const MemberStatus = z.enum(['invited', 'active', 'disabled']);
export type MemberStatus = z.infer<typeof MemberStatus>;

export const ArtistStatus = z.enum(['active', 'archived']);
export type ArtistStatus = z.infer<typeof ArtistStatus>;

/** Gig lifecycle (§13.1). */
export const GigStatus = z.enum([
  'draft',
  'published',
  'live',
  'intermission',
  'ended',
  'archived',
  'cancelled',
  'suspended',
]);
export type GigStatus = z.infer<typeof GigStatus>;

/** Interaction kinds (§7, §27.11). */
export const InteractionType = z.enum(['song_request', 'freeform', 'improv_brief']);
export type InteractionType = z.infer<typeof InteractionType>;

/** Moderation state, stored separately from content (§19.3). */
export const ModerationState = z.enum(['clean', 'flagged', 'blocked']);
export type ModerationState = z.infer<typeof ModerationState>;

/** Performer-facing interaction status (§18.7). */
export const PerformerStatus = z.enum([
  'new',
  'reviewed',
  'shortlisted',
  'accepted',
  'queued',
  'performed',
  'declined',
  'skipped',
  'hidden',
]);
export type PerformerStatus = z.infer<typeof PerformerStatus>;

/** Coarse audience-visible status projection (§18.7). */
export const PublicStatus = z.enum(['received', 'selected', 'played', 'not_selected']);
export type PublicStatus = z.infer<typeof PublicStatus>;

/** Set-list public visibility modes (§17.3). */
export const SetlistVisibilityMode = z.enum([
  'hidden',
  'section_names_only',
  'current_only',
  'current_and_next',
  'remaining_in_current_set',
  'full_upcoming',
  'full_with_played_history',
]);
export type SetlistVisibilityMode = z.infer<typeof SetlistVisibilityMode>;

/** Set-list item types (§17.1). */
export const SetlistItemType = z.enum([
  'song',
  'improv_slot',
  'announcement',
  'break',
  'custom',
]);
export type SetlistItemType = z.infer<typeof SetlistItemType>;

export const SetlistItemStatus = z.enum(['planned', 'current', 'played', 'skipped']);
export type SetlistItemStatus = z.infer<typeof SetlistItemStatus>;

/** Cooldown policy kinds (§18.6). */
export const CooldownKind = z.enum([
  'none',
  'minutes',
  'until_next_set',
  'until_end',
  'once_per_gig',
]);
export type CooldownKind = z.infer<typeof CooldownKind>;

/** Improv audience modes (§20.1). */
export const AudienceMode = z.enum(['general', 'guided', 'advanced']);
export type AudienceMode = z.infer<typeof AudienceMode>;

/** Per-dimension artist policy for Create a Song (§20.2). */
export const FieldPolicyKind = z.enum([
  'hidden',
  'fixed',
  'general',
  'guided',
  'exact',
  'random',
  'artist_decides',
]);
export type FieldPolicyKind = z.infer<typeof FieldPolicyKind>;

/** Resolver policy for unresolved improv values (§20.10). */
export const ResolverPolicy = z.enum([
  'artist_review',
  'deterministic_default',
  'seeded_random',
  'audience_exact',
]);
export type ResolverPolicy = z.infer<typeof ResolverPolicy>;

/** Tip lifecycle (§27.20). Provider is authority; success only via webhook (§22.6). */
export const TipStatus = z.enum([
  'created',
  'processing',
  'succeeded',
  'failed',
  'refunded',
  'disputed',
]);
export type TipStatus = z.infer<typeof TipStatus>;

export const PaymentProvider = z.enum(['stripe', 'external_link', 'interac', 'none']);
export type PaymentProvider = z.infer<typeof PaymentProvider>;

export const PaymentConnectionStatus = z.enum(['disconnected', 'onboarding', 'ready', 'restricted']);
export type PaymentConnectionStatus = z.infer<typeof PaymentConnectionStatus>;

export const NotificationProvider = z.enum(['ntfy']);
export type NotificationProvider = z.infer<typeof NotificationProvider>;

export const NotificationChannelStatus = z.enum(['unverified', 'ready', 'degraded', 'disabled']);
export type NotificationChannelStatus = z.infer<typeof NotificationChannelStatus>;

export const DeliveryStatus = z.enum(['pending', 'sent', 'failed', 'dead_letter']);
export type DeliveryStatus = z.infer<typeof DeliveryStatus>;

/** Configuration scopes for feature overrides, highest-to-lowest precedence resolved in domain (§10). */
export const FeatureScope = z.enum([
  'platform',
  'environment',
  'plan',
  'workspace',
  'artist',
  'template',
  'gig',
  'live_session',
]);
export type FeatureScope = z.infer<typeof FeatureScope>;

export const RiskLevel = z.enum(['low', 'medium', 'high']);
export type RiskLevel = z.infer<typeof RiskLevel>;

export const ActorType = z.enum(['user', 'audience', 'system', 'platform_operator', 'provider']);
export type ActorType = z.infer<typeof ActorType>;
